import { z } from 'zod'
import { ITEM_TYPE_OPTIONS } from './constants'
import { MarketplaceStatusSchema } from './constants'

// --- Transformation & Preprocessing Helpers ---
export const booleanToNumber = (value: boolean): 1 | 0 => (value ? 1 : 0)
export const numberToBoolean = (value: number): boolean => value === 1

export const stringToBoolean = (value: string): boolean => value === '1'

const parsePrice = (value: string | number): string => {
  const cleanValue = typeof value === 'string' ? value.replace(/,/g, '') : value
  return parseFloat(String(cleanValue)).toFixed(2)
}

export const ProductDurationSchema = z.enum(['specific', 'period'])
export type ProductDurations = z.infer<typeof ProductDurationSchema>
export const ProductDurationNumberToStringSchema = z
  .number()
  .transform((value) => {
    if (value === 1) {
      return 'period'
    }
    return 'specific'
  })

const priceIsValid = (value: string) => !isNaN(parseFloat(value))
const usageQuantityPreprocess = (value: unknown) =>
  value === undefined || value === null || value === ''
    ? undefined
    : Number(value)
const salesTaxPreprocess = (value: unknown) => {
  if (value === '' || value === null || value === undefined) return undefined
  const numberValue = Number(value)
  return isNaN(numberValue) ? undefined : numberValue
}

// --- Main Schema ---
export function getConfigureStoreSchema(isNew: boolean) {
  return z
    .object({
      // --- Marketplace & Basic Info ---
      marketplace: MarketplaceStatusSchema,
      name: z.string().min(1, 'Product Name is required'),
      program_item_id: z.string().min(1, 'Item is required'),
      global_item_category_id: z.string().min(1, 'Category is required'),
      global_item_id: z.string(),
      // --- Pricing ---
      product_type: z.number(),
      price: z
        .union([z.string(), z.number()])
        .transform(parsePrice)
        .refine(priceIsValid, {
          message: 'Price must be a valid numeric value',
        }),
      sales_tax: z.preprocess(
        salesTaxPreprocess,
        z.number().min(0).max(100).optional()
      ),

      // --- Description & Tags ---
      description: z.string().max(1000, 'Description is too long').optional(),
      comments: z.string().optional(),
      keyword_tags: z.array(z.string()).optional(),

      // --- File Upload ---
      file: isNew
        ? z.instanceof(File, { message: 'Image is required' })
        : z.instanceof(File).optional().nullable(),

      // --- Usage & Duration ---
      usage_quantity: z.preprocess(
        usageQuantityPreprocess,
        z.number().min(1).optional()
      ),
      duration_type: ProductDurationSchema.optional(),
      duration_unit: z.string().optional(),
      specific_duration: z.string().optional(),

      // --- Dates ---
      start_date: z.string().optional(),
      end_date: z.string().nullable().optional(),

      // --- Subscription ---
      is_subscription: z.boolean().optional(),
      type: z.number().nullable().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.product_type === ITEM_TYPE_OPTIONS.SERVICE) {
        if (!data.duration_type) {
          ctx.addIssue({
            path: ['duration_type'],
            code: z.ZodIssueCode.custom,
            message: 'Duration type is required for this product type',
          })
        }

        if (data.duration_type === 'specific') {
          if (
            !data.duration_unit ||
            !['days', 'weeks', 'months'].includes(data.duration_unit)
          ) {
            ctx.addIssue({
              path: ['duration_unit'],
              code: z.ZodIssueCode.custom,
              message:
                'Unit of Measure is required when duration type is specific',
            })
          }

          const duration = data.specific_duration

          if (!duration) {
            ctx.addIssue({
              path: ['specific_duration'],
              code: z.ZodIssueCode.custom,
              message:
                'Specific duration must be greater than 0 when duration type is specific',
            })
          }

          const durationAsNumber = Number.parseInt(duration ?? '')
          if (!isNaN(durationAsNumber) && durationAsNumber <= 0) {
            ctx.addIssue({
              path: ['specific_duration'],
              code: z.ZodIssueCode.custom,
              message: 'Duration is required',
            })
          }
        }

        if (data.duration_type === 'period') {
          if (!data.start_date) {
            ctx.addIssue({
              path: ['start_date'],
              code: z.ZodIssueCode.custom,
              message:
                'The start date field is required when duration type is period.',
            })
          }

          if (!data.is_subscription && !data.end_date) {
            ctx.addIssue({
              path: ['end_date'],
              code: z.ZodIssueCode.custom,
              message: 'The end date field is required.',
            })
          }
        }

        if (data.start_date && data.end_date) {
          const start = new Date(data.start_date)
          const end = new Date(data.end_date)
          if (start > end) {
            ctx.addIssue({
              path: ['start_date'],
              code: z.ZodIssueCode.custom,
              message: 'Start date cannot be after end date.',
            })
          }
        }
      }
    })
}

export function getTransformedProductSchema(isNew: boolean) {
  return getConfigureStoreSchema(isNew).transform((values) => {
    const is_subscription = values.is_subscription
      ? booleanToNumber(values.is_subscription)
      : undefined

    const specific_duration =
      values.duration_type === 'specific' ? values.specific_duration : undefined

    return {
      ...values,
      is_subscription,
      specific_duration,
    }
  })
}

export type TransformedProduct = z.infer<
  ReturnType<typeof getTransformedProductSchema>
>

// --- Custom Validations ---

// --- Types ---
export type ConfigureStoreFormValues = z.infer<
  ReturnType<typeof getConfigureStoreSchema>
>
export type ConfigureStoreFormProps = {
  onSubmit: (data: ConfigureStoreFormValues) => void
}

export const ProductDurationTextSchema = z.union([
  z.literal('period'),
  z.literal('specific'),
])

export type ConfigureStoreEditValues = Omit<
  ConfigureStoreFormValues,
  'file'
> & {
  saved_image_path?: string
}
